import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Autocomplete,
    Box,
    CircularProgress,
} from "@mui/material";
import { Email } from "@mui/icons-material";
import customHttp from "../../services/customHTTP";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import Swal from "sweetalert2";

const NotifyPurchaser = ({ data, mode = "cart_products" }) => {
    const { user } = useAuth();
    const { selectedSite, selectedSiteData } = useSite();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [userSites, setUserSites] = useState([])
    const [dialogOpen, setDialogOpen] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [users, setUsers] = useState([]);
    const [formData, setFormData] = useState({
        users: [],
        description: "",
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (dialogOpen) {
            console.log("Current User: ", user)
            const currentUserSitestemp = user?.user_sites || "";
            const userSitesArray = currentUserSitestemp
                .split(",")       // split by comma
                .map(s => s.trim()) // remove spaces
                .filter(Boolean);
            setUserSites(userSitesArray || []);

            fetchData("/users", setAllUsers);
        }
    }, [dialogOpen])

    useEffect(() => {
        if (allUsers.length > 0 && userSites.length > 0 && dialogOpen) {
            // 1. Remove user_id = 1
            const filtered = allUsers.filter(u => u.user_id !== 1 && u.user_role !== "Viewer");

            // 2. Compare user sites with current user's sites
            const matched = filtered.filter(u => {
                const userSitesArr = (u.user_sites || "")
                    .split(",")
                    .map(s => s.trim())
                    .filter(Boolean);
                return userSitesArr.some(site => userSites.includes(site));
            });

            // 3. Map to Autocomplete-ready objects
            const userOptions = matched.map(u => ({
                id: u.user_id,
                name: u.user_display_name || `${u.user_first_name} ${u.user_last_name}`,
                email: u.user_email,
                user_owner: u.user_role === "Admin" ? "BPA Customer Support" : "",
            }));

            console.log("Final Autocomplete Users:", userOptions);
            setUsers(userOptions);
        }
    }, [allUsers, userSites, dialogOpen]);


    const fetchData = async (endpoint, setState) => {
        try {
            const response = await customHttp.get(endpoint);
            if (response.data.data.length > 0) {
                console.log(response.data.data);
                setState(response.data.data);
            } else {
                setError(`Failed to fetch data: No data found for ${endpoint}.`);
            }
        } catch (err) {
            setError(`Failed to fetch data from ${endpoint}: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    /** Handle field changes */
    const handleFormChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    /** Validation */
    const validateForm = () => {
        const newErrors = {};
        if (!formData.users || formData.users.length === 0) {
            newErrors.users = "Please select at least one user";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /** Handle submit */
    const handleSubmit = async () => {
        if (!validateForm()) return;
        const emailString = formData.users.map(u => u.email).join(",");
        const reqBody = { mode, user_emails: emailString, currentUserId: user?.user_id, currentUserName: user?.user_display_name, currentUserEmail: user?.user_email, selectedSiteData, data, description: formData?.description }
        console.log(reqBody)
        setLoading(true);
        try {
            const response = await customHttp.post("/request_list/notifyUsers", reqBody);
            if (response.data && response.data.success) {
                setLoading(false);
                handleDialogClose();
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: response.data.message,
                    confirmButtonText: "Okay",
                });
            } else {
                setLoading(false);
                handleDialogClose();
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response.data.message,
                    confirmButtonText: "Okay",
                });
            }
        } catch (error) {
            setLoading(false);
            handleDialogClose();
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error,
                confirmButtonText: "Okay",
            });
        }

    };

    /** Close dialog */
    const handleDialogClose = () => {
        setDialogOpen(false);
        setFormData({ users: [], description: "" });
        setErrors({});
    };

    return (
        <>
            {/* Trigger Button */}
            <Button
                variant="contained"
                color="primary"
                startIcon={<Email />}
                maxWidth={mode === "cart_products" ? "100%" : "175px"}
                minWidth={"175px"}
                className={"checkout-button"}
                onClick={() => setDialogOpen(true)}
            >
                Notify Purchaser
            </Button>

            {/* Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                sx={{
                    "& .MuiDialog-paper": {
                        width: "600px",
                        height: "auto",
                        padding: "16px",
                    },
                }}
            >
                <DialogContent>
                    <Typography variant="h6">
                        Notify Purchasers / Customer Support
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Select the Users you wish to notify and enter some description
                    </Typography>
                    {loading && !error ? (<Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} height={"250px"}>
                        <CircularProgress />
                    </Box>) : !loading && error ? (
                        <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} height={"250px"}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* User Autocomplete with Grouping & Custom Rendering */}
                            <Autocomplete
                                multiple
                                options={users.sort((a, b) => {
                                    // Push unassigned users to the top
                                    if (!a.user_owner && b.user_owner) return -1;
                                    if (a.user_owner && !b.user_owner) return 1;
                                    return a.user_owner.localeCompare(b.user_owner);
                                })}
                                groupBy={(option) =>
                                    option.user_owner && option.user_owner.trim()
                                        ? option.user_owner
                                        : "Unassigned"
                                }
                                getOptionLabel={(option) => option.name}
                                value={formData.users}
                                onChange={(_, value) => handleFormChange("users", value)}
                                renderGroup={(params) =>
                                    params.group === "Unassigned" ? (
                                        // For unassigned users, just render list items (no header)
                                        <ul key={params.key} style={{ paddingLeft: 0, margin: 0 }}>
                                            {params.children}
                                        </ul>
                                    ) : (
                                        <li key={params.key}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: "bold",
                                                    backgroundColor: "#f5f5f5",
                                                    padding: "4px 8px",
                                                    mt: 2

                                                }}
                                            >
                                                {params.group}
                                            </Typography>
                                            <ul style={{ margin: 0 }}>
                                                {params.children}
                                            </ul>
                                        </li>
                                    )
                                }
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Box>
                                            <Typography variant="body1">{option.name}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {option.email}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Users to Notify"
                                        required
                                        error={!!errors.users}
                                        helperText={errors.users}
                                        sx={{ mb: 2 }}
                                    />
                                )}
                            />

                            {/* Description Field */}
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Description"

                                value={formData.description}
                                onChange={(e) => handleFormChange("description", e.target.value)}

                            />
                        </>
                    )}

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading ? true : false}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default NotifyPurchaser;
